import React from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import marked from "marked";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

interface MarkdownViewerProps {
  content: string;
  fontSize?: string;
  fontColor?: string;
  backgroundColor?: string;
  blurNumbers?: boolean; // Optional prop for blur numbers
  noBoldHeadings?: boolean; // Optional prop to bypass heading styling
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  content,
  fontSize = "sm",
  fontColor,
  backgroundColor = "white",
  blurNumbers,
  noBoldHeadings = false,
}) => {
  // Process blur syntax before markdown parsing

  // new function to blur any numbers in the content
  const processNumbers = (text: string): string => {
    if (!blurNumbers) return text; // If blurNumbers is false, return the text unchanged
    // Replace numbers with <span class="blur-text">number</span>
    return text.replace(/(\d+)/g, '<span class="blur-text">$1</span>');
  };

  const processBlurSyntax = (text: string): string => {
    // Replace ~~text~~ with <span class="blur-text">text</span>
    return text.replace(/~~(.*?)~~/g, '<span class="blur-text">$1</span>');
  };

  const processedContent = processBlurSyntax(processNumbers(content));
  const parsedMarkdown = marked(processedContent);
  const sanitizedHtml = DOMPurify.sanitize(parsedMarkdown, {
    ADD_ATTR: ["target"],
  });
  const bgColorDefault = useColorModeValue("white", "gray.800");
  const fontColorDefault = useColorModeValue("primary.darkGray", "gray.400");

  return (
    <Box
      className="markdown-content"
      fontSize={fontSize}
      color={fontColor || fontColorDefault}
      bg={backgroundColor === "transparent" ? "transparent" : backgroundColor || bgColorDefault}
      sx={{
        ...(!noBoldHeadings && {
          "& h1, & h2, & h3, & h4, & h5, & h6, & b": {
            fontWeight: "bold",
          },
        }),
        ...(noBoldHeadings && {
          "& h1, & h2, & h3, & h4, & h5, & h6, & b": {
            fontWeight: "normal",
          },
        }),
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          marginBottom: "0.3rem",
        },
        "& p": {
          marginBottom: "0.5rem",
        },
        "& ul, & ol": {
          marginLeft: "1.5rem",
          marginBottom: "0.5rem",
        },
        "& blockquote": {
          borderLeft: "4px solid",
          borderColor: "gray.200",
          paddingLeft: "1rem",
          marginY: "1rem",
        },
        "& code": {
          bg: "gray.100",
          padding: "0.2rem",
          borderRadius: "0.2rem",
        },
        "& pre": {
          bg: "gray.100",
          padding: "1rem",
          borderRadius: "0.5rem",
          marginY: "1rem",
          overflowX: "auto",
        },
        "& .blur-text": {
          filter: "blur(3px)",
          transition: "filter 0.3s ease",
        },
      }}>
      {parse(sanitizedHtml)}
    </Box>
  );
};
